import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { get } from '../../api';
import convertToQueryParams from '../../utils/convertToQueryParams';

const CATEGORIES_API = 'inventory/categories/';

/** The list endpoint is paginated (DRF default 10) — page through it rather
 *  than treating one page as the whole catalog. */
export const CATEGORIES_PAGE_SIZE = 20;

/** `assigned=true` responses are buyer-specific, so their cache entries have to
 *  be too — otherwise the next buyer to log in on this browser reads the
 *  previous one's categories. */
function useCategoryCacheScope(assigned) {
  const { user } = useSelector((state) => state.user);
  return assigned ? String(user?.user?.id ?? 'anonymous') : 'all';
}

export function useCategoriesInfiniteQuery(filters = {}) {
  const {
    is_active = true,
    search = null,
    parent = null,
    parent__isnull = null,
    assigned = null,
    ordering = 'name',
    size = CATEGORIES_PAGE_SIZE,
    enabled = true,
  } = filters;

  const scope = useCategoryCacheScope(assigned);

  const query = useInfiniteQuery({
    queryKey: [
      'categories',
      'infinite',
      scope,
      { is_active, search, parent, parent__isnull, assigned, ordering, size },
    ],
    queryFn: async ({ pageParam }) => {
      const response = await get(
        `${CATEGORIES_API}${convertToQueryParams({
          is_active,
          search,
          parent,
          parent__isnull,
          assigned,
          ordering,
          page: pageParam,
          size,
        })}`
      );
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage?.next ? lastPageParam + 1 : undefined,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

  return {
    ...query,
    categories: query.data?.pages?.flatMap((page) => page.results || []) ?? [],
    totalCount: query.data?.pages?.[0]?.count ?? 0,
  };
}

/** Slug validity via the list endpoint, which answers 200 with `count: 0` for
 *  an unknown slug. The detail route 404s, and `get()` toasts on any non-200.
 *  Scoped to active + assigned so a deactivated or unassigned category's slug is
 *  rejected and stripped from the URL, matching what the menus actually offer. */
export function useCategorySlugValidQuery(slug) {
  const scope = useCategoryCacheScope(true);

  return useQuery({
    queryKey: ['categories', 'slug-valid', scope, slug],
    queryFn: async () => {
      const response = await get(
        `${CATEGORIES_API}${convertToQueryParams({
          slug,
          is_active: true,
          assigned: true,
          size: 1,
        })}`
      );
      return (response.data?.count ?? 0) > 0;
    },
    enabled: Boolean(slug?.trim()),
    staleTime: 5 * 60 * 1000,
  });
}
